import React, { JSX, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { validateTokenFunctionURL } from "../../Constants";
import "./TokenValidator.scss";
import "../../fonts/fonts.css"
import logo from "../../resources/logo.svg"

// Import all your components
import DatenAA from "../DatenAA/DatenAA";
import DatenAI from "../DatenAI/DatenAI";
import DatenIA from "../DatenIA/DatenIA";
import DatenII from "../DatenII/DatenII";
import EinrichtungMitMS from "../EinrichtungMitMS/EinrichtungMitMS";
import EinrichtungOhneMS from "../EinrichtungOhneMS/EinrichtungOhneMS";
import EinrichtungSmartwatch from "../EinrichtungSmart/EinrichtungSmart";
import EinrichtungStarklarAnd from "../EinrichtungStarklarAnd/EinrichtungStarklarAnd";
import EinrichtungStarklarIOS from "../EinrichtungStartklarIOS/EinrichtungStarklarIOS";
import SoftwareMcAfee from "../SoftwareMcAfee/SoftwareMcAfee";
import { Skeleton } from "@mui/material";
import EmailForm from "../EmailForm/EmailForm";
import EmailConfirmation from "../EmailConfirmation/EmailConfirmation";
import { decryptQueryParam } from "../../services/FormService";
import MyInformation from "../MyInformation/MyInformation";

const getComponentByProcessId = (processID: number, commonProps: any) => {
    const routes: { [key: string]: JSX.Element } = {
        "1": <DatenAI {...commonProps} />,
        "2": <DatenIA {...commonProps} />,
        "3": <DatenII {...commonProps} />,
        "4": <DatenAA {...commonProps} />,
        "5": <EinrichtungMitMS {...commonProps} />,
        "6": <EinrichtungOhneMS {...commonProps} />,
        "7": <EinrichtungSmartwatch {...commonProps} />,
        "8": <EinrichtungStarklarAnd {...commonProps} />,
        "9": <EinrichtungStarklarIOS {...commonProps} />,
        "10": <SoftwareMcAfee {...commonProps} />,
    };

    return routes[processID.toString()] || null;
};

interface TokenValidatorProps {
    isConfirmationRoute?: boolean;
    isMyInformationRoute?: boolean;
}

const TokenValidator: React.FC<TokenValidatorProps> = ({ isConfirmationRoute = false, isMyInformationRoute = false }) => {
    const { tokenID, email } = useParams<{ tokenID: string; email?: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submissionStatus, setSubmissionStatus] = useState<string | null>(null);
    const [ComponentToRender, setComponentToRender] = useState<JSX.Element | null>(null);

    const handleFormSubmissionStatus = (status: string) => {
        setSubmissionStatus(status);
    };

    useEffect(() => {
        const validateToken = async () => {
            try {
                if (isConfirmationRoute && email) {
                    const decodedEmail = decodeURIComponent(decryptQueryParam(email));
                    setComponentToRender(
                        <EmailConfirmation
                            tokenID={tokenID!}
                            email={decodedEmail}
                        />
                    );
                    setLoading(false);
                    return;
                }

                if (isMyInformationRoute) {
                    setComponentToRender(
                        <MyInformation />
                    );
                    setLoading(false);
                    return;
                }

                const response = await axios.get(
                    validateTokenFunctionURL!, {
                    params: {
                        tokenID,
                    }
                });

                if (response.data.valid) {
                    const isEmailConfirmed = response.data.isEmailConfirmed;
                    const processID = parseInt(response.data.processId);
                    const confirmedEmail = response.data.email;

                    if (!isEmailConfirmed) {
                        setComponentToRender(<EmailForm />);
                        setLoading(false);
                        return;
                    }

                    const commonProps = {
                        onSubmitCallback: handleFormSubmissionStatus,
                        tokenID: tokenID!,
                        confirmedEmail,
                        processID,
                    };

                    setComponentToRender(getComponentByProcessId(processID, commonProps));
                } else {
                    setError("Ungültiger oder abgelaufener Vorgang");
                }
            } catch (err: any) {
                setError(err.response?.data.message || err.response?.data || "Vorgang ist nicht verfügbar");
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [tokenID, isConfirmationRoute, email, isMyInformationRoute]);

    return (
        <div className="validation-form">
            <img src={logo} alt="Company Logo" className="logo" />
            <h2 className="app-title">SMARTBAR</h2>
            {loading ? (
                <>
                    <Skeleton className="skeleton" variant="text" height={80} animation="wave" />
                    <Skeleton className="skeleton" variant="rounded" height={270} animation="wave" />
                    <Skeleton className="skeleton" variant="text" height={120} animation="wave" />
                    <Skeleton className="skeleton" variant="text" height={50} animation="wave" />
                    <Skeleton className="skeleton" variant="text" height={50} animation="wave" />
                    <Skeleton className="skeleton" variant="rounded" height={110} animation="wave" />
                    <Skeleton className="skeleton" variant="text" height={50} animation="wave" />
                    <Skeleton className="skeleton" variant="text" height={50} animation="wave" />
                </>
            ) : error ? (
                <div className="error-message">{error}</div>
            ) : submissionStatus ? (
                <div className={`status-message ${submissionStatus.includes('Fehler') ? 'error' : 'success'}`}>
                    <span dangerouslySetInnerHTML={{ __html: submissionStatus }} />
                </div>
            ) : (
                ComponentToRender
            )}
        </div>
    );
};

export default TokenValidator;